import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { CommandBar } from "../components/CommandBar";
import { HelpChatButton } from "../components/HelpChat/HelpChatButton";
import { HelpChatPanel } from "../components/HelpChat/HelpChatPanel";
import { useHelpChat } from "../components/HelpChat/useHelpChat";
import { NavigationRail } from "../components/NavigationRail";
import { getAttentionSummary, type AttentionSummary } from "../faturas/faturasApi";
import { getTaskSummary } from "../tarefas/tarefasApi";
import { toIsoDate } from "../tarefas/taskDates";
import { NewVersionBanner } from "../components/NewVersionBanner";
import { SettingsDialog, ThemeMode } from "../components/SettingsDialog";
import { useNewVersionDetector } from "../hooks/useNewVersionDetector";
import { getUserProfile, updateThemeMode } from "../profile/profileApi";
import {
  NavigationItemId,
  navigationItemIds,
  userCanAccessNav,
} from "../navigation/navigation";

const themeStorageKey = "themeMode";

const getSystemTheme = (): "light" | "dark" => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const resolveTheme = (mode: ThemeMode): "light" | "dark" =>
  mode === "system" ? getSystemTheme() : mode;

const getStoredThemeMode = (): ThemeMode => {
  const stored = localStorage.getItem(themeStorageKey);
  return stored === "dark" || stored === "light" || stored === "system"
    ? stored
    : "light";
};

const resolveActiveItem = (pathname: string): NavigationItemId => {
  const segment = pathname.replace(/^\/+/, "").split("/")[0];
  if (!segment) return "dashboard";
  return navigationItemIds.has(segment as NavigationItemId)
    ? (segment as NavigationItemId)
    : "dashboard";
};

export function ShellLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuth();
  // Chat de ajuda e Definições vivem fora do menu (não passam pelo userCanAccessNav), por isso
  // gate-se aqui pela mesma autoridade do backend: /api/ai e /api/settings estão sob a feature
  // "definicoes". Um trabalhador só de Folha de Horas não a tem — deixa de ver os dois.
  const isAdminUser = user?.roles?.includes("Administrator") ?? false;
  const canUseDefinicoes = isAdminUser || (user?.features?.includes("definicoes") ?? false);
  const helpChat = useHelpChat();
  const { newVersionAvailable, reload: reloadApp, dismiss: dismissBanner } = useNewVersionDetector();
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  // O que está à espera de alguém (faturas por validar, anexos ignorados): o número vem ter
  // com o utilizador no menu, em vez de ele ir aos ecrãs "só para ver se está tudo bem".
  const [attention, setAttention] = useState<AttentionSummary | null>(null);
  const [tasksDue, setTasksDue] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [themeMode, setThemeMode] = useState<ThemeMode>(
    getStoredThemeMode,
  );
  const [appliedTheme, setAppliedTheme] = useState<
    "light" | "dark"
  >(() => resolveTheme(getStoredThemeMode()));
  const [toastMessage, setToastMessage] = useState<string | null>(
    null,
  );
  const toastTimeoutRef = useRef<number | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(
    null,
  );

  const activeItem = useMemo(
    () => resolveActiveItem(location.pathname),
    [location.pathname],
  );
  const isAuthScreen = location.pathname.startsWith("/auth");

  // Cada contador só se pede a quem tem a área: sem isto, quem não tem Faturas ou Tarefas levava
  // um 403 a cada 5 minutos, a cada foco da janela e a cada mudança de ecrã.
  const canSeeInvoices = userCanAccessNav(isAdminUser, user?.features, "faturas");
  const canSeeTasks = userCanAccessNav(isAdminUser, user?.features, "tarefas");

  useEffect(() => {
    if (isAuthScreen || !canSeeInvoices) return;
    let cancelled = false;
    const refresh = () => {
      getAttentionSummary()
        .then((summary) => {
          if (cancelled) return;
          setAttention(summary);
          // Os ecrãs que explicam o número (ex.: Faturas Despesas) ouvem isto em vez de o pedirem
          // outra vez ao servidor.
          window.dispatchEvent(new CustomEvent("aja2:attention-summary", { detail: summary }));
        })
        .catch(() => {
          // Sem badge; o menu continua a valer.
        });
    };
    refresh();
    const interval = setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener("focus", refresh);
    // Quem muda o que está pendente (validar, apagar) anuncia-o: o número desce na hora,
    // não só quando se navega.
    window.addEventListener("aja2:attention", refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("aja2:attention", refresh);
    };
    // location.pathname: ao mudar de ecrã o número atualiza (ex.: acabou de validar).
  }, [isAuthScreen, canSeeInvoices, location.pathname]);

  useEffect(() => {
    if (isAuthScreen || !canSeeTasks) return;
    let cancelled = false;
    const refresh = () => {
      getTaskSummary(toIsoDate(new Date()))
        .then((summary) => {
          if (!cancelled) setTasksDue(summary.mineDueOrOverdue);
        })
        .catch(() => {
          // Sem badge.
        });
    };
    refresh();
    const interval = setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener("focus", refresh);
    window.addEventListener("aja2:tasks", refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("aja2:tasks", refresh);
    };
  }, [isAuthScreen, canSeeTasks, location.pathname]);

  useEffect(() => {
    if (themeMode !== "system") {
      setAppliedTheme(themeMode);
      return;
    }

    const mediaQuery = window.matchMedia?.(
      "(prefers-color-scheme: dark)",
    );
    const applySystemTheme = () => {
      setAppliedTheme(
        mediaQuery && mediaQuery.matches ? "dark" : "light",
      );
    };

    applySystemTheme();

    if (!mediaQuery) return;

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", applySystemTheme);
      return () => {
        mediaQuery.removeEventListener("change", applySystemTheme);
      };
    }

    mediaQuery.addListener(applySystemTheme);
    return () => {
      mediaQuery.removeListener(applySystemTheme);
    };
  }, [themeMode]);

  useEffect(() => {
    const root = document.documentElement;
    if (appliedTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [appliedTheme]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const loadUserProfile = async () => {
      if (!isAuthenticated) {
        setUserDisplayName(null);
        return;
      }

      try {
        const profile = await getUserProfile();
        if (!isMounted) return;
        const firstName = profile.firstName?.trim() ?? "";
        setUserDisplayName(firstName || profile.email || null);
      } catch {
        if (!isMounted) return;
        setUserDisplayName(user?.email ?? null);
      }
    };

    void loadUserProfile();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.email]);

  useEffect(() => {
    const state = location.state;
    if (!state || typeof state !== "object") {
      return;
    }

    if (!("toast" in state)) {
      return;
    }

    const message = (state as { toast?: string }).toast;
    if (!message) {
      return;
    }

    setToastMessage(message);

    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }

    toastTimeoutRef.current = window.setTimeout(() => {
      setToastMessage(null);
      toastTimeoutRef.current = null;
    }, 2500);

    const nextState = { ...(state as Record<string, unknown>) };
    delete (nextState as { toast?: string }).toast;

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null,
    });
  }, [location, navigate]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleThemeModeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
    localStorage.setItem(themeStorageKey, mode);
    if (isAuthenticated) {
      void updateThemeMode(mode).catch((error) => {
        console.error("Erro ao atualizar o tema:", error);
      });
    }
  };

  const handleNavigate = (item: NavigationItemId) => {
    navigate(`/${item}`);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/auth/login");
    }
  };

  const handleCommandNavigate = (target: string) => {
    const nextPath = `/${target}`;
    if (target === "perfil") {
      navigate(nextPath, { state: { from: location } });
      return;
    }
    navigate(nextPath);
  };

  const toastStyle =
    appliedTheme === "dark"
      ? {
          backgroundColor:
            "color-mix(in oklab, var(--color-green-500) 18%, var(--color-black))",
          border:
            "1px solid color-mix(in oklab, var(--color-green-400) 30%, var(--color-black))",
          color: "var(--color-green-400)",
        }
      : {
          backgroundColor:
            "color-mix(in oklab, var(--color-green-500) 12%, var(--color-white))",
          border:
            "1px solid color-mix(in oklab, var(--color-green-500) 35%, var(--color-white))",
          color: "var(--color-green-700)",
        };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background">
      {isMobileMenuOpen && !isAuthScreen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {!isAuthScreen && (
        <NavigationRail
          activeItem={activeItem}
          onNavigate={handleNavigate}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
          badges={{
            tarefas: { count: tasksDue, label: "tarefas suas para hoje ou atrasadas" },
            faturas: { count: attention?.pendingValidation ?? 0, label: "faturas por validar" },
            "importacoes-email": { count: attention?.importsToReview ?? 0, label: "linhas da importação por decidir" },
          }}
        />
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {newVersionAvailable && (
          <NewVersionBanner onReload={reloadApp} onDismiss={dismissBanner} />
        )}
        {!isAuthScreen && (
          <div className="shell-topbar">
            <CommandBar
              canOpenSettings={canUseDefinicoes}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onToggleMobileMenu={() =>
                setIsMobileMenuOpen(!isMobileMenuOpen)
              }
              onNavigate={handleCommandNavigate}
              onLogout={handleLogout}
              userLabel={userDisplayName || undefined}
            />
          </div>
        )}

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      <SettingsDialog
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        themeMode={themeMode}
        onThemeModeChange={handleThemeModeChange}
      />

      {toastMessage && (
        <div
          className="fixed z-50"
          role="status"
          aria-live="polite"
          style={{ top: "4.5rem", right: "1.5rem" }}
        >
          <div
            className="px-4 py-3 shadow-lg"
            style={{ borderRadius: "var(--radius-card)", ...toastStyle }}
          >
            {toastMessage}
          </div>
        </div>
      )}

      {!isAuthScreen && isAuthenticated && canUseDefinicoes && (
        <>
          {helpChat.isOpen && (
            <HelpChatPanel
              messages={helpChat.messages}
              isLoading={helpChat.isLoading}
              onSend={helpChat.sendMessage}
              onClose={helpChat.close}
            />
          )}
          <HelpChatButton isOpen={helpChat.isOpen} onClick={helpChat.toggle} />
        </>
      )}
    </div>
  );
}
